import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { addContact } from '../../../api/firebase/firestore/contact';
import { sendMailToInquiry } from '../../../api/sendgrid';
import {
  black,
  blackText,
  blueGrayText,
  cardBorder,
  gray,
  primaryButtonBackground,
  primarycolor,
  secondaryButtonnBackground
} from '../../../constants/Colors';
import {
  cardContentPadding,
  cardSize,
  titleFontSize,
  titleMb
} from '../../../constants/Location';
import { categories } from '../../../constants/enum';
import { throwError } from '../../../modules/error';
import { sendSlackContact } from '../../../modules/slack';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import Header from '../../uiParts/Header';

const styles = {
  button: {
    '@media screen and (max-width:480px)': {
      width: 148
    },
    height: 42,
    width: 192
  },
  buttonWrapper: {
    display: 'flexbox',
    justifyContent: 'space-between'
  },
  cardBorder,
  cardContentPadding,
  cardSize,
  data: {
    color: black,
    fontSize: '16px',
    whiteSpace: 'pre-line'
  },
  field: {
    marginBottom: '40px'
  },
  icon: {
    color: gray,
    height: '20px',
    width: '20px'
  },
  label: {
    color: blueGrayText,
    fontSize: '12px',
    marginBottom: '8px'
  },
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb
};

export interface ContactType {
  companyName: string;
  email: string;
  phone: string;
  category: string;
  message: string;
  contactKind: string;
  contactNumbers: string;
  type: string;
}

const ContactConfirm: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationInfo = location.state as ContactType;

  useEffect(() => {
    document.title = t('title.common.contactConfirm');
  });

  const backPage = (): void => {
    navigate(`/common/contact`, {
      state: {
        category: Number(locationInfo.category),
        companyName: locationInfo.companyName,
        contactKind: locationInfo.contactKind,
        contactNumbers: locationInfo.contactNumbers,
        email: locationInfo.email,
        message: locationInfo.message,
        phone: locationInfo.phone,
        type: locationInfo.type
      }
    });
  };

  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const { type } = locationInfo;
      await addContact(
        locationInfo.companyName,
        locationInfo.email,
        locationInfo?.phone,
        Number(locationInfo.category),
        locationInfo.message
      );
      await sendMailToInquiry(locationInfo, contactCategoryHelper());
      window.localStorage.clear();
      navigate(`/common/contact_confirm_complete?type=${type}`);
    } catch (e) {
      setOpen(true);
      setErrorText(t('validation.error.general'));
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  const contactCategoryHelper = (): string => {
    const number = Number(locationInfo.category);
    if (number === categories.service) {
      return t('contact.label1');
    } else if (number === categories.search) {
      return t('contact.label3');
    } else if (number === categories.media) {
      return t('contact.label7');
    } else if (number === categories.market) {
      return t('contact.label2');
    } else if (number === categories.feature) {
      return t('contact.label10');
    } else if (number === categories.login) {
      return t('contact.label9');
    } else if (number === categories.client) {
      return t('contact.label13');
    } else if (number === categories.agent) {
      return t('contact.label8');
    } else if (number === categories.opinion) {
      return t('contact.label11');
    } else if (number === categories.oneMedia) {
      return t('contact.label5');
    } else if (number === categories.multiMedia) {
      return t('contact.label6');
    } else if (number === categories.other) {
      return t('contact.label12');
    }
    return '';
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Header />
      <Typography
        component="div"
        sx={{ marginBottom: '40px', marginTop: '24px' }}
      >
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Grid>
            <Card
              sx={[
                styles.cardSize,
                styles.cardBorder,
                { marginBottom: '40px' }
              ]}
            >
              <CardContent sx={styles.cardContentPadding}>
                <Typography sx={[styles.titleMb, styles.title]} component="div">
                  {t('contact.contact')}
                </Typography>
                <Typography component="div" sx={styles.field}>
                  <Typography sx={styles.label}>
                    {t('contact.companyName')}*
                  </Typography>
                  <Typography sx={styles.data}>
                    {locationInfo.companyName}
                  </Typography>
                </Typography>
                <Typography component="div" sx={styles.field}>
                  <Typography sx={styles.label}>{t('form.email')}*</Typography>
                  <Typography sx={styles.data}>{locationInfo.email}</Typography>
                </Typography>
                <Typography component="div" sx={styles.field}>
                  <Typography sx={styles.label}>
                    {t('contact.phone')}
                  </Typography>
                  <Typography sx={styles.data}>
                    {locationInfo.phone !== 'undefined'
                      ? locationInfo.phone
                      : ''}
                  </Typography>
                </Typography>
                <Typography component="div" sx={styles.field}>
                  <Typography sx={styles.label}>
                    {t('contact.category')}*
                  </Typography>
                  <Grid container direction="row" alignItems="center">
                    <RadioButtonCheckedIcon sx={styles.icon} />
                    <Typography sx={styles.data}>
                      {contactCategoryHelper()}
                    </Typography>
                  </Grid>
                </Typography>
                <Typography component="div" sx={styles.field}>
                  <Typography sx={styles.label}>
                    {t('contact.message')}*
                  </Typography>
                  <Typography sx={styles.data}>
                    {locationInfo.message}
                  </Typography>
                </Typography>
                <CardActions
                  sx={{
                    '&.MuiCardActions-root': {
                      marginTop: '30px'
                    },
                    padding: 0
                  }}
                >
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    sx={styles.buttonWrapper}
                  >
                    <Button
                      // Disabled={buttonDisabledFlag()}
                      variant="contained"
                      sx={[secondaryButtonnBackground, styles.button]}
                      onClick={() => {
                        void (() => {
                          backPage();
                        })();
                      }}
                    >
                      <Typography
                        component="div"
                        color={primarycolor}
                        sx={{ fontSize: 15 }}
                      >
                        {t('contact.edit')}
                      </Typography>
                    </Button>
                    <Button
                      // Disabled={buttonDisabledFlag()}
                      variant="contained"
                      sx={[primaryButtonBackground, styles.button]}
                      onClick={() => {
                        void (async () => {
                          await onSubmit();
                          sendSlackContact(
                            locationInfo,
                            contactCategoryHelper()
                          );
                        })();
                      }}
                    >
                      <Typography
                        component="div"
                        color="white"
                        sx={{ fontSize: 15 }}
                      >
                        {t('contact.send')}
                      </Typography>
                    </Button>
                  </Grid>
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <CustomSnackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          text={errorText}
        />
      </Typography>
    </>
  );
};

export default ContactConfirm;
